import React, { useState } from 'react';
import { Button, FileInput, Label } from 'flowbite-react';
import ExcelComparisonTable from '../components/ExcelComparisonTable';
import { parseExcelFile, compareData } from '../helpers/comparisonLogic';

const SummaryCard = ({ title, value, colorClass }: { title: string, value: string | number, colorClass: string }) => (
    <div className={`p-4 text-white rounded shadow-sm ${colorClass} flex flex-col justify-between`}>
        <div className="text-sm font-bold">{title}</div>
        <div className="text-2xl font-bold mt-2">{value}</div>
    </div>
);

const ExcelComparison = () => {
    const [reportFile, setReportFile] = useState<File | null>(null);
    const [gdevFile, setGdevFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [comparisonResult, setComparisonResult] = useState<any>(null);
    const [errorMsg, setErrorMsg] = useState('');

    const handleCompare = async () => {
        if (!reportFile || !gdevFile) {
            setErrorMsg("Please upload both Excel files before comparing.");
            return;
        }

        setErrorMsg('');
        setLoading(true);

        try {
            const reportData = await parseExcelFile(reportFile);
            const gdevData = await parseExcelFile(gdevFile);

            const result = compareData(reportData, gdevData);
            setComparisonResult(result);
        } catch (error) {
            console.error("Error parsing files:", error);
            setErrorMsg("An error occurred while parsing the Excel files. Please ensure they are valid.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="p-2 md:p-10 bg-white dark:border-gray-700 dark:bg-gray-800 rounded-2xl">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold dark:text-gray-300">
                        Excel Comparison Report
                    </h1>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 bg-gray-50 p-4 rounded-lg dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                    <div>
                        <div className="mb-2 block">
                            <Label htmlFor="report-file" value="Upload Report Form Excel" />
                        </div>
                        <FileInput 
                            id="report-file" 
                            accept=".xlsx, .xls, .csv" 
                            onChange={(e) => setReportFile(e.target.files?.[0] || null)} 
                        />
                    </div>
                    <div>
                        <div className="mb-2 block">
                            <Label htmlFor="gdev-file" value="Upload GDEV Excel" />
                        </div>
                        <FileInput 
                            id="gdev-file" 
                            accept=".xlsx, .xls, .csv" 
                            onChange={(e) => setGdevFile(e.target.files?.[0] || null)} 
                        />
                    </div>
                    <div className="flex items-end">
                        <Button 
                            color="success" 
                            onClick={handleCompare} 
                            disabled={loading || !reportFile || !gdevFile}
                            className="w-full"
                        >
                            {loading ? 'Comparing...' : 'Compare Data'}
                        </Button>
                    </div>
                </div>

                {errorMsg && (
                    <div className="text-red-600 mb-4 font-semibold">
                        {errorMsg}
                    </div>
                )}

                {comparisonResult && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            <SummaryCard title="Total Report" value={comparisonResult.totalReport} colorClass="bg-blue-600" />
                            <SummaryCard title="Total GDEV" value={comparisonResult.totalGDEV} colorClass="bg-green-600" />
                            <SummaryCard title="Missing" value={comparisonResult.missing} colorClass="bg-red-600" />
                            <SummaryCard title="Mismatch" value={comparisonResult.mismatch} colorClass="bg-orange-500" />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                            <SummaryCard title="MATCHED MRE" value={comparisonResult.matchedMRE} colorClass="bg-green-600" />
                            <SummaryCard title="MATCHED OCALC" value={comparisonResult.matchedOCALC} colorClass="bg-blue-600" />
                            <SummaryCard title="FILTERED REPORT DATA" value={comparisonResult.filteredReportCount} colorClass="bg-red-600" />
                        </div>

                        <div className="space-y-8">
                            <ExcelComparisonTable 
                                title="Report Form Comparison" 
                                rowData={comparisonResult.reportTableData} 
                                isGdevTable={false}
                            />
                            <ExcelComparisonTable 
                                title="GDEV Comparison" 
                                rowData={comparisonResult.gdevTableData} 
                                isGdevTable={true}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ExcelComparison;
